/**
 * ProductionStageDigest — a pivot-style report (Activity-Digest look & feel)
 * that counts ACTIVE order forms across the production checklist stages,
 * grouped Customer-wise or Goldsmith-wise.
 *
 * Multi-bucket counting: an order appears in every column where that stage is
 * its current in-progress step. The Total column is a DISTINCT count of the
 * entity's pending order forms. Every cell shows an order-form count and (for
 * running-number companies) a distinct Page-No count, and opens a drill-down
 * of the underlying order forms grouped by Page No.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import AppShell from '@/components/AppShell';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { extractError } from '@/lib/errors';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from '@/components/ui/command';
import {
  Users, Hammer, ChevronRight, ChevronDown, RefreshCw, Info, Inbox, Layers,
  ChevronsUpDown, Check, X, Clock,
} from 'lucide-react';
import {
  COLUMN_KEYS, STAGE_GROUPS, COLUMN_GROUP, GROUP_FIRST_COLS, STAGE_STYLE,
  RM_SUBGROUP_FIRST_COLS, buildPivot, buildTiles,
} from '@/components/production-digest/stageDigestConfig';
import StageDigestDrilldownDialog from '@/components/production-digest/StageDigestDrilldownDialog';

// Bold divider at each stage-group boundary; thin divider between sub-columns.
// Wave 4.10f — Within Raw Material, each side-material sub-block starts with a
// distinct coloured 2px divider so Diamonds / Colour Stones / Pearls / No
// Requirement read as separate blocks.
const leafBorder = (colKey) => {
  if (RM_SUBGROUP_FIRST_COLS[colKey]) return `border-l-2 ${RM_SUBGROUP_FIRST_COLS[colKey]}`;
  if (GROUP_FIRST_COLS.has(colKey)) return `border-l-2 ${STAGE_STYLE[COLUMN_GROUP[colKey]].headBar}`;
  return 'border-l border-[var(--divider)]';
};

const TOTAL_BORDER = 'border-l-2 border-[var(--gold-accent)]';

export default function ProductionStageDigest() {
  const { effectiveCompanyId } = useAuth();
  return (
    <AppShell
      title="Production Stage Digest"
      subtitle="Active order forms at each production stage — Customer-wise or Goldsmith-wise. Click any number to see the order forms."
    >
      <ReportBody companyId={effectiveCompanyId} />
    </AppShell>
  );
}

function ReportBody({ companyId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [dim, setDim] = useState('customer'); // 'customer' | 'goldsmith'
  const [expanded, setExpanded] = useState(() => new Set());
  const [selCust, setSelCust] = useState([]);   // selected customer filter values ('__none__' = unassigned)
  const [selGold, setSelGold] = useState([]);   // selected goldsmith filter values
  const [drill, setDrill] = useState({ open: false, title: '', subtitle: '', orders: [], columnKey: '' });

  // Wave 4.10i — live auto-refresh. The digest reloads itself every 5 minutes
  // and the "Refresh" button shows a countdown to the next tick. Auto (and
  // manual) refreshes are SILENT: they refetch the data without clearing the
  // user's filters / expanded rows and without flashing the full-page skeleton.
  // Only the first load (and a company switch) resets filters + shows the
  // skeleton. A manual refresh restarts the 5-minute window.
  const REFRESH_MS = 5 * 60 * 1000;
  const [lastRefreshedAt, setLastRefreshedAt] = useState(() => Date.now());
  const [nowTs, setNowTs] = useState(() => Date.now());
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async ({ silent }) => {
    if (silent) setRefreshing(true); else setLoading(true);
    if (!silent) setError(null);
    try {
      const params = {};
      if (companyId) params.company_id = companyId;
      const { data } = await api.get('/reports/production-stage-digest', { params });
      setPayload(data);
      if (!silent) {
        setExpanded(new Set());
        setSelCust([]);
        setSelGold([]);
        setError(null);
      }
      setLastRefreshedAt(Date.now());
    } catch (e) {
      // On a silent/background refresh, keep the data already on screen rather
      // than wiping it out with an error card.
      if (!silent) {
        setError(extractError(e, 'Failed to load the Production Stage Digest.'));
        setPayload(null);
      }
    } finally {
      if (silent) setRefreshing(false); else setLoading(false);
    }
  }, [companyId]);

  const load = useCallback(() => fetchData({ silent: false }), [fetchData]);
  const refresh = useCallback(() => fetchData({ silent: true }), [fetchData]);

  // Initial load + reload on company switch.
  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 5 min (silent). The window restarts whenever
  // ``lastRefreshedAt`` changes (e.g. a manual refresh) so the next auto tick
  // is always a full 5 minutes away.
  useEffect(() => {
    const id = window.setInterval(refresh, REFRESH_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, lastRefreshedAt]);

  // 1-second tick powering the countdown label.
  useEffect(() => {
    const id = window.setInterval(() => setNowTs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const msLeft = Math.max(0, REFRESH_MS - (nowTs - lastRefreshedAt));
  const countdownLabel = `${Math.floor(msLeft / 60000)}:${String(Math.floor((msLeft % 60000) / 1000)).padStart(2, '0')}`;

  const orders = useMemo(() => payload?.orders || [], [payload]);
  const usesRef = !!payload?.uses_reference_numbers;
  // This report always labels the running number as "Page No" (the counts are
  // distinct page numbers), independent of a company's custom reference_label.
  const refLabel = 'Page No.';

  // Stable label maps (from the full, unfiltered set) so chips always resolve.
  const custLabelMap = useMemo(() => {
    const m = {};
    distinctOptions(orders, 'customer').forEach((o) => { m[o.value] = o.label; });
    return m;
  }, [orders]);
  const goldLabelMap = useMemo(() => {
    const m = {};
    distinctOptions(orders, 'goldsmith').forEach((o) => { m[o.value] = o.label; });
    return m;
  }, [orders]);

  // Cascading filter options: each combo's list reflects the OTHER selection.
  const customerOptions = useMemo(
    () => distinctOptions(orders.filter((o) => selGold.length === 0 || selGold.includes(o.goldsmith_id || '__none__')), 'customer'),
    [orders, selGold],
  );
  const goldsmithOptions = useMemo(
    () => distinctOptions(orders.filter((o) => selCust.length === 0 || selCust.includes(o.customer_id || '__none__')), 'goldsmith'),
    [orders, selCust],
  );

  // Orders after applying the two multi-select filters.
  const filteredOrders = useMemo(
    () => orders.filter((o) =>
      (selCust.length === 0 || selCust.includes(o.customer_id || '__none__')) &&
      (selGold.length === 0 || selGold.includes(o.goldsmith_id || '__none__'))),
    [orders, selCust, selGold],
  );
  const filtered = selCust.length > 0 || selGold.length > 0;

  const toggleCust = (v) => { setSelCust((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v])); setExpanded(new Set()); };
  const toggleGold = (v) => { setSelGold((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v])); setExpanded(new Set()); };

  const rows = useMemo(() => buildPivot(filteredOrders, dim), [filteredOrders, dim]);
  const tiles = useMemo(() => buildTiles(filteredOrders), [filteredOrders]);
  const grand = useMemo(() => {
    const pages = new Set();
    for (const o of filteredOrders) if (o.reference_no != null && o.reference_no !== '') pages.add(String(o.reference_no));
    return { count: filteredOrders.length, pages: pages.size };
  }, [filteredOrders]);

  // Wave 4.10g — Per-column totals shown in the table header. Distinct order
  // forms + distinct pages (reference numbers) that fall in each column across
  // every top-level row, matching the count/"N pg" format used in the body.
  // An order can sit in several parallel columns, so column totals can sum to
  // more than the grand Total (same rule as the body cells).
  const colTotals = useMemo(() => {
    const res = {};
    for (const colKey of COLUMN_KEYS) {
      const seen = new Set();
      const pages = new Set();
      for (const row of rows) {
        const agg = row.cells?.[colKey];
        if (!agg) continue;
        for (const o of (agg.orders || [])) {
          const id = o.id || o._id || o.order_no;
          if (id == null || seen.has(id)) continue;
          seen.add(id);
          if (o.reference_no != null && o.reference_no !== '') pages.add(String(o.reference_no));
        }
      }
      res[colKey] = { count: seen.size, pages: pages.size };
    }
    return res;
  }, [rows]);

  const clearFilters = () => { setSelCust([]); setSelGold([]); setExpanded(new Set()); };

  const toggleRow = (key) => setExpanded((prev) => {
    const n = new Set(prev);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });

  const openDrill = (list, title, subtitle, columnKey = '') =>
    setDrill({ open: true, title, subtitle: subtitle || '', orders: list || [], columnKey: columnKey || '' });

  const primaryLabel = dim === 'customer' ? 'Customer' : 'Goldsmith';
  const PrimaryIcon = dim === 'customer' ? Users : Hammer;
  const SecondaryIcon = dim === 'customer' ? Hammer : Users;
  const secondaryLabel = dim === 'customer' ? 'Goldsmith' : 'Customer';

  return (
    <div className="space-y-4" data-testid="production-stage-digest">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-[var(--divider)] bg-white p-0.5 shadow-sm" data-testid="stage-digest-dim-toggle">
          {[
            { k: 'customer', label: 'Customer-wise', Icon: Users },
            { k: 'goldsmith', label: 'Goldsmith-wise', Icon: Hammer },
          ].map(({ k, label, Icon }) => (
            <button
              key={k}
              type="button"
              onClick={() => { setDim(k); setExpanded(new Set()); }}
              aria-pressed={dim === k}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-accent)] ${
                dim === k ? 'bg-[var(--ink-navy)] text-white shadow-sm' : 'text-slate-600 hover:bg-[var(--paper-warm)]'
              }`}
              data-testid={`stage-digest-dim-${k}`}
            >
              <Icon size={14} />{label}
            </button>
          ))}
        </div>

        {/* Cascading, multi-select Customer + Goldsmith filters */}
        <div className="flex flex-wrap items-center gap-2" data-testid="stage-digest-filters">
          <FilterCombobox
            testid="stage-digest-filter-customer"
            Icon={Users}
            placeholder="All Customers"
            noun="customer"
            selected={selCust}
            onToggle={toggleCust}
            onClear={() => { setSelCust([]); setExpanded(new Set()); }}
            options={customerOptions}
          />
          <FilterCombobox
            testid="stage-digest-filter-goldsmith"
            Icon={Hammer}
            placeholder="All Goldsmiths"
            noun="goldsmith"
            selected={selGold}
            onToggle={toggleGold}
            onClear={() => { setSelGold([]); setExpanded(new Set()); }}
            options={goldsmithOptions}
          />
          {filtered && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-[12px] font-semibold text-[var(--goldsmith-icon)] transition-colors hover:bg-[var(--paper-warm)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-accent)]"
              data-testid="stage-digest-filter-clear"
            >
              <X size={13} /> Clear all
            </button>
          )}
        </div>

        <div className="ml-auto flex items-center gap-3 text-[12px] text-slate-500">
          {payload?.scope === 'sales' && payload?.sales_customer_count != null && (
            <span className="inline-flex items-center gap-1 text-[var(--gold-icon)]" data-testid="stage-digest-sales-note">
              <Info size={12} /> Limited to your {payload.sales_customer_count} attributed customer{payload.sales_customer_count === 1 ? '' : 's'}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-500" data-testid="stage-digest-refresh-timer" title="Time until the next automatic refresh">
            <Clock size={13} className="text-slate-400" />
            Auto-refresh in
            <span className="font-mono tabular-nums text-[var(--ink-navy)]">{countdownLabel}</span>
          </span>
          <button
            type="button"
            onClick={refresh}
            disabled={loading || refreshing}
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--divider)] bg-white px-2.5 py-1.5 font-medium text-[var(--ink-navy)] transition-colors hover:bg-[var(--paper-warm)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-accent)] disabled:opacity-60"
            data-testid="stage-digest-refresh"
            title="Refresh now (also resets the 5-minute timer)"
          >
            <RefreshCw size={13} className={loading || refreshing ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Selected-filter chips (removable) */}
      {filtered && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="stage-digest-chips">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Filters:</span>
          {selCust.map((v) => (
            <FilterChip
              key={`c-${v}`}
              Icon={Users}
              label={custLabelMap[v] || 'Unassigned'}
              tone="customer"
              onRemove={() => toggleCust(v)}
              testid="stage-digest-chip-customer"
            />
          ))}
          {selGold.map((v) => (
            <FilterChip
              key={`g-${v}`}
              Icon={Hammer}
              label={goldLabelMap[v] || 'Unassigned'}
              tone="goldsmith"
              onRemove={() => toggleGold(v)}
              testid="stage-digest-chip-goldsmith"
            />
          ))}
        </div>
      )}

      {loading ? (
        <LoadingState />
      ) : error ? (
        <Card className="flex flex-col items-center gap-3 border-rose-200 bg-rose-50/40 px-4 py-10 text-center" data-testid="stage-digest-error">
          <div className="text-sm font-medium text-rose-700">{error}</div>
          <button onClick={load} className="rounded-md bg-[var(--ink-navy)] px-3 py-1.5 text-[13px] font-semibold text-white">Try again</button>
        </Card>
      ) : orders.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 border-dashed px-4 py-14 text-center" data-testid="stage-digest-empty">
          <Inbox size={26} className="text-slate-300" />
          <div className="text-sm font-medium text-[var(--ink-navy)]">No active order forms</div>
          <div className="text-xs text-slate-500">Every order in this scope is completed, delivered back, cancelled, or on hold.</div>
        </Card>
      ) : filteredOrders.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 border-dashed px-4 py-14 text-center" data-testid="stage-digest-no-matches">
          <Inbox size={26} className="text-slate-300" />
          <div className="text-sm font-medium text-[var(--ink-navy)]">No order forms match the selected filters</div>
          <button onClick={clearFilters} className="mt-1 inline-flex items-center gap-1 rounded-md bg-[var(--ink-navy)] px-3 py-1.5 text-[13px] font-semibold text-white">
            <X size={13} /> Clear all filters
          </button>
        </Card>
      ) : (
        <>
          {/* Stage tiles */}
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 lg:grid-cols-8" data-testid="stage-digest-tiles">
            {tiles.map((t) => {
              const st = STAGE_STYLE[t.key];
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => openDrill(t.orders, t.label, `${t.count} active order form${t.count === 1 ? '' : 's'} at this stage`, t.key)}
                  disabled={t.count === 0}
                  className={`rounded-xl border bg-white px-3 py-2.5 text-left transition-shadow ${st.tile} ${t.count === 0 ? 'opacity-45' : `cursor-pointer hover:shadow-md focus-visible:outline-none focus-visible:ring-2 ${st.ring}`}`}
                  style={{ borderLeftWidth: 4 }}
                  data-testid={`stage-digest-tile-${t.key}`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className={`inline-flex h-2 w-2 rounded-full ${st.dot}`} />
                    <span className="text-[10px] font-semibold uppercase tracking-wide leading-tight text-slate-600">{t.label}</span>
                  </div>
                  <div className="mt-1 font-mono text-[22px] font-bold tabular-nums text-[var(--ink-navy)]">{t.count}</div>
                  {usesRef && t.pages > 0 && (
                    <div className="text-[10px] font-medium text-slate-400 tabular-nums">{t.pages} {refLabel.replace(/\.$/, '')}{t.pages === 1 ? '' : 's'}</div>
                  )}
                </button>
              );
            })}
            {/* Total tile */}
            <button
              type="button"
              onClick={() => openDrill(orders, 'All Pending Order Forms', `${grand.count} distinct active order form${grand.count === 1 ? '' : 's'}`, 'total')}
              className={`rounded-xl border bg-[var(--ink-navy)] px-3 py-2.5 text-left text-white transition-shadow cursor-pointer hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-accent)]`}
              style={{ borderLeftWidth: 4, borderLeftColor: 'var(--gold-accent)' }}
              data-testid="stage-digest-tile-total"
            >
              <div className="flex items-center gap-1.5">
                <Layers size={12} className="text-[var(--gold-accent)]" />
                <span className="text-[10px] font-semibold uppercase tracking-wide leading-tight text-[var(--gold-accent-soft)]">Total Pending</span>
              </div>
              <div className="mt-1 font-mono text-[22px] font-bold tabular-nums">{grand.count}</div>
              {usesRef && grand.pages > 0 && (
                <div className="text-[10px] font-medium text-[var(--gold-accent-soft)] tabular-nums">{grand.pages} {refLabel.replace(/\.$/, '')}{grand.pages === 1 ? '' : 's'}</div>
              )}
            </button>
          </div>

          {/* Pivot table */}
          <div className="overflow-x-auto rounded-xl border border-[var(--divider)] bg-white shadow-sm">
            <table className="w-full border-collapse text-[12.5px]" data-testid="stage-digest-table">
              <thead>
                {/* Row 1 — group headers */}
                <tr>
                  <th
                    rowSpan={2}
                    className="sticky left-0 z-[2] min-w-[220px] bg-[var(--ink-navy)] px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wide text-white"
                  >
                    {primaryLabel}
                  </th>
                  {STAGE_GROUPS.map((g) => {
                    const st = STAGE_STYLE[g.key];
                    const border = leafBorder(g.cols[0].key);
                    if (g.cols.length === 1) {
                      const t = colTotals[g.cols[0].key] || { count: 0, pages: 0 };
                      return (
                        <th key={g.key} rowSpan={2} className={`px-2 py-2 text-center text-[10.5px] font-bold uppercase tracking-wide ${st.head} ${border}`}>
                          <div>{g.label}</div>
                          <HeaderTotal count={t.count} pages={t.pages} usesRef={usesRef} tone={st.count} />
                        </th>
                      );
                    }
                    return (
                      <th key={g.key} colSpan={g.cols.length} className={`px-2 py-2 text-center text-[10.5px] font-bold uppercase tracking-wide ${st.head} ${border}`}>
                        {g.label}
                      </th>
                    );
                  })}
                  <th rowSpan={2} className={`px-2 py-2 text-center text-[10.5px] font-bold uppercase tracking-wide ${STAGE_STYLE.total.head} ${TOTAL_BORDER}`}>
                    <div>Total</div>
                    <HeaderTotal count={grand.count} pages={grand.pages} usesRef={usesRef} tone="text-[var(--ink-navy)]" emphasize />
                  </th>
                </tr>
                {/* Row 2 — sub-column headers (multi-col groups only) */}
                <tr>
                  {STAGE_GROUPS.filter((g) => g.cols.length > 1).flatMap((g) => {
                    const st = STAGE_STYLE[g.key];
                    return g.cols.map((c) => {
                      const t = colTotals[c.key] || { count: 0, pages: 0 };
                      return (
                        <th key={c.key} className={`px-2 py-1.5 text-center text-[9.5px] font-semibold uppercase tracking-tight ${st.head} ${leafBorder(c.key)}`}>
                          <div>{c.short}</div>
                          <HeaderTotal count={t.count} pages={t.pages} usesRef={usesRef} tone={st.count} />
                        </th>
                      );
                    });
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => {
                  const isOpen = expanded.has(row.key);
                  const hasChildren = row.children.length > 0;
                  // Expanded parent reads as a SUBTOTAL row: champagne tint +
                  // bold numbers, with its (normal-weight) breakdown beneath.
                  const rowBg = isOpen ? 'bg-[var(--champagne)]' : (ri % 2 ? 'bg-[var(--paper-warm)]' : 'bg-white');
                  const numWeight = isOpen ? 'font-bold' : 'font-medium';
                  const totalWeight = isOpen ? 'font-extrabold' : 'font-bold';
                  return (
                    <React.Fragment key={row.key}>
                      <tr className={`${rowBg} border-t border-[var(--divider)] transition-colors ${isOpen ? '' : 'hover:bg-[var(--diamond-bg)]/40'}`} data-testid="stage-digest-row" data-expanded={isOpen ? 'true' : 'false'}>
                        <td className={`sticky left-0 z-[1] ${rowBg} px-2 py-1.5`}>
                          <button
                            type="button"
                            onClick={() => hasChildren && toggleRow(row.key)}
                            className={`flex w-full items-center gap-1.5 text-left ${hasChildren ? 'cursor-pointer' : 'cursor-default'}`}
                            data-testid="stage-digest-row-toggle"
                          >
                            {hasChildren
                              ? (isOpen ? <ChevronDown size={14} className="shrink-0 text-[var(--gold-icon)]" /> : <ChevronRight size={14} className="shrink-0 text-slate-400" />)
                              : <span className="w-3.5 shrink-0" />}
                            <PrimaryIcon size={13} className="shrink-0 text-[var(--goldsmith-icon)]" />
                            <span className={`truncate text-[var(--ink-navy)] ${isOpen ? 'font-bold' : 'font-semibold'}`} title={row.label}>{row.label}</span>
                            {hasChildren && <span className="ml-auto shrink-0 pl-2 text-[10px] font-medium text-slate-400 tabular-nums">{row.children.length} {secondaryLabel.toLowerCase()}{row.children.length === 1 ? '' : 's'}</span>}
                          </button>
                        </td>
                        {COLUMN_KEYS.map((colKey) => (
                          <CountCell
                            key={colKey}
                            agg={row.cells[colKey]}
                            groupKey={COLUMN_GROUP[colKey]}
                            usesRef={usesRef}
                            borderCls={leafBorder(colKey)}
                            weightCls={numWeight}
                            onOpen={() => openDrill(row.cells[colKey].orders, `${row.label} · ${labelForCol(colKey)}`, `${primaryLabel}: ${row.label}`, colKey)}
                          />
                        ))}
                        <TotalCell
                          agg={row.total}
                          usesRef={usesRef}
                          weightCls={totalWeight}
                          onOpen={() => openDrill(row.total.orders, `${row.label} · Total Pending`, `${primaryLabel}: ${row.label}`, 'total')}
                        />
                      </tr>
                      {isOpen && row.children.map((ch) => (
                        <tr key={ch.key} className="border-t border-dashed border-[var(--divider)] bg-white/80 hover:bg-[var(--paper-warm)]" data-testid="stage-digest-child-row">
                          <td className="sticky left-0 z-[1] bg-white px-2 py-1.5">
                            <div className="flex items-center gap-1.5 pl-6">
                              <SecondaryIcon size={12} className="shrink-0 text-slate-400" />
                              <span className="truncate text-[12px] font-normal text-slate-600" title={ch.label}>{ch.label}</span>
                            </div>
                          </td>
                          {COLUMN_KEYS.map((colKey) => (
                            <CountCell
                              key={colKey}
                              agg={ch.cells[colKey]}
                              groupKey={COLUMN_GROUP[colKey]}
                              usesRef={usesRef}
                              borderCls={leafBorder(colKey)}
                              muted
                              weightCls="font-normal"
                              onOpen={() => openDrill(ch.cells[colKey].orders, `${ch.label} · ${labelForCol(colKey)}`, `${secondaryLabel}: ${ch.label} · ${primaryLabel}: ${row.label}`, colKey)}
                            />
                          ))}
                          <TotalCell
                            agg={ch.total}
                            usesRef={usesRef}
                            muted
                            weightCls="font-medium"
                            onOpen={() => openDrill(ch.total.orders, `${ch.label} · Total Pending`, `${secondaryLabel}: ${ch.label} · ${primaryLabel}: ${row.label}`, 'total')}
                          />
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-slate-400">
            Showing {rows.length} {primaryLabel.toLowerCase()}{rows.length === 1 ? '' : 's'} · {grand.count} active order forms. Numbers can exceed the Total because an order can sit in two parallel stages at once (design + raw material).
          </div>
        </>
      )}

      <StageDigestDrilldownDialog
        open={drill.open}
        onOpenChange={(o) => setDrill((d) => ({ ...d, open: o }))}
        title={drill.title}
        subtitle={drill.subtitle}
        orders={drill.orders}
        columnKey={drill.columnKey}
        groupBy={dim}
        companyId={companyId}
        usesReferenceNumbers={usesRef}
        referenceLabel={refLabel}
      />
    </div>
  );
}

function labelForCol(colKey) {
  for (const g of STAGE_GROUPS) {
    const c = g.cols.find((x) => x.key === colKey);
    if (c) return g.cols.length > 1 ? `${g.label}: ${c.short}` : g.label;
  }
  return colKey;
}

// Column total rendered inside a header cell: distinct order forms on top and
// the page (reference) count beneath — same shape the body cells use.
function HeaderTotal({ count, pages, usesRef, tone, emphasize }) {
  return (
    <div className="mt-1 flex flex-col items-center leading-none normal-case" data-testid="stage-digest-col-total">
      <span className={`font-mono tabular-nums ${emphasize ? 'text-[14px] font-extrabold' : 'text-[13px] font-bold'} ${count > 0 ? tone : 'text-slate-300'}`}>
        {count}
      </span>
      {usesRef && pages > 0 && (
        <span className="mt-0.5 text-[8px] font-semibold tabular-nums text-slate-400">{pages} pg</span>
      )}
    </div>
  );
}

function CountCell({ agg, groupKey, usesRef, borderCls, onOpen, muted, weightCls = 'font-bold' }) {
  const st = STAGE_STYLE[groupKey];
  const count = agg?.count || 0;
  const pages = agg?.pages || 0;
  return (
    <td className={`px-2 py-1 text-center align-middle ${borderCls}`}>
      {count > 0 ? (
        <button
          type="button"
          onClick={onOpen}
          className={`inline-flex min-w-[38px] flex-col items-center rounded-md px-1.5 py-1 leading-none transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 ${st.ring}`}
          data-testid="stage-digest-cell"
          title="Click to see order forms"
        >
          <span className={`font-mono text-[15px] ${weightCls} tabular-nums ${muted ? 'text-slate-600' : st.count}`}>{count}</span>
          {usesRef && pages > 0 && <span className="mt-0.5 text-[9px] font-medium text-slate-400 tabular-nums">{pages} pg</span>}
        </button>
      ) : (
        <span className="text-slate-300">—</span>
      )}
    </td>
  );
}

function TotalCell({ agg, usesRef, onOpen, muted, weightCls = 'font-extrabold' }) {
  const count = agg?.count || 0;
  const pages = agg?.pages || 0;
  return (
    <td className={`px-2 py-1 text-center align-middle ${TOTAL_BORDER}`}>
      {count > 0 ? (
        <button
          type="button"
          onClick={onOpen}
          className="inline-flex min-w-[40px] flex-col items-center rounded-md px-1.5 py-1 leading-none transition-colors hover:bg-[var(--champagne)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-accent)]"
          data-testid="stage-digest-total-cell"
          title="Click to see all pending order forms"
        >
          <span className={`font-mono text-[15px] ${weightCls} tabular-nums text-[var(--ink-navy)]`}>{count}</span>
          {usesRef && pages > 0 && <span className="mt-0.5 text-[9px] font-semibold text-[var(--gold-accent)] tabular-nums">{pages} pg</span>}
        </button>
      ) : (
        <span className="text-slate-300">—</span>
      )}
    </td>
  );
}

// Distinct {value,label} options for a dimension (unassigned -> '__none__').
function distinctOptions(list, kind) {
  const idKey = kind === 'customer' ? 'customer_id' : 'goldsmith_id';
  const nameKey = kind === 'customer' ? 'customer_name' : 'goldsmith_name';
  const m = new Map();
  for (const o of list) {
    const v = o[idKey] || '__none__';
    if (!m.has(v)) m.set(v, o[nameKey] || 'Unassigned');
  }
  return Array.from(m.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

// Searchable, cascading, MULTI-SELECT filter combobox (Popover + Command).
function FilterCombobox({ testid, Icon, placeholder, noun, selected, onToggle, onClear, options }) {
  const [open, setOpen] = useState(false);
  const count = selected.length;
  const triggerLabel = count === 0
    ? placeholder
    : `${count} ${noun}${count === 1 ? '' : 's'} selected`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}
          className="inline-flex min-w-[190px] max-w-[240px] cursor-pointer items-center gap-1.5 rounded-lg border border-[var(--divider)] bg-white px-3 py-1.5 text-[13px] font-medium text-[var(--ink-navy)] shadow-sm transition-colors hover:bg-[var(--paper-warm)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-accent)]"
          data-testid={testid}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <Icon size={14} className="shrink-0 text-[var(--goldsmith-icon)]" />
          <span className={`truncate ${count ? '' : 'font-normal text-slate-400'}`}>{triggerLabel}</span>
          {count > 0 ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              className="ml-auto shrink-0 rounded p-0.5 text-slate-400 hover:bg-[var(--divider)] hover:text-[var(--ink-navy)]"
              aria-label="Clear filter"
              data-testid={`${testid}-clear`}
            >
              <X size={13} />
            </button>
          ) : (
            <ChevronsUpDown size={13} className="ml-auto shrink-0 text-slate-400" />
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder={`Search ${noun}s...`} />
          <CommandList>
            <CommandEmpty>No matches.</CommandEmpty>
            {count > 0 && (
              <CommandGroup>
                <CommandItem value="__clear__" onSelect={() => onClear()} data-testid={`${testid}-option-all`}>
                  <X className="mr-2 h-3.5 w-3.5 text-[var(--goldsmith-icon)]" />
                  Clear selection ({count})
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup>
              {options.map((o) => {
                const isSel = selected.includes(o.value);
                return (
                  <CommandItem key={o.value} value={o.label} onSelect={() => onToggle(o.value)} data-testid={`${testid}-option`}>
                    <span className={`mr-2 flex h-3.5 w-3.5 items-center justify-center rounded-[3px] border ${isSel ? 'border-[var(--ink-navy)] bg-[var(--ink-navy)] text-white' : 'border-slate-300'}`}>
                      {isSel && <Check className="h-2.5 w-2.5" />}
                    </span>
                    <span className="truncate">{o.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Removable selected-filter chip.
function FilterChip({ Icon, label, tone, onRemove, testid }) {
  const toneCls = tone === 'customer'
    ? 'border-sky-300 bg-sky-50 text-sky-800'
    : 'border-amber-300 bg-amber-50 text-amber-800';
  return (
    <span className={`inline-flex max-w-[220px] items-center gap-1 rounded-full border px-2 py-0.5 text-[12px] font-medium ${toneCls}`} data-testid={testid}>
      <Icon size={11} className="shrink-0 opacity-70" />
      <span className="truncate">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="shrink-0 rounded-full p-0.5 opacity-70 transition-opacity hover:bg-black/10 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-accent)]"
        aria-label={`Remove ${label}`}
        data-testid={`${testid}-remove`}
      >
        <X size={11} />
      </button>
    </span>
  );
}

function LoadingState() {
  return (
    <div className="space-y-4" data-testid="stage-digest-loading">
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 lg:grid-cols-8">
        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-[76px] rounded-xl" />)}
      </div>
      <Skeleton className="h-[320px] w-full rounded-xl" />
    </div>
  );
}
